import PropTypes from 'prop-types';
import React, { Fragment, PureComponent } from 'react';
import { styled } from '@compiled/react';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';
import ChangeboardingManager from '@confluence-classic/confluence-changeboarding/src/ChangeboardingManager.js';

import AtlaskitAnalyticsListener from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';

import { CreateDialogContextProvider } from '@confluence/create-dialog-context';
import { ChangeEditionLegacyCoordinator } from '@confluence/change-edition/entry-points/ChangeEditionLegacyCoordinator.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { getIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/getIsNav4Enabled.ts';
import { CopySpacePermissionsForLegacyNewSpace } from '@confluence/copy-space-permissions/entry-points/CopySpacePermissionsForLegacyNewSpace.ts';
import { Screen, SPACE_OVERVIEW } from '@confluence/screen';
import {
	PRODUCT_HOME_ID,
	CONTAINER_HOME_ID,
	CONTAINER_ADMIN_HOME_ID,
	ADMIN_HOME_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { PageLayout } from '@confluence/page-layout/entry-points/PageLayout.ts';
import { EditSpaceLogo } from '@confluence/edit-space-logo';
import { EditPageLoadingContextProvider } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { NotificationLandedEvent } from '@confluence/notifications/entry-points/NotificationLandedEvent.ts';
import { EXTERNAL_SHARE } from '@confluence/named-routes/entry-points/routes.ts';

import SingleModalContainer from '../SingleModal/index.js';
import shortcutContext from '../KeyboardShortcuts/shortcutContextDecorator.js';
import { StandaloneRestrictedPageOrSpace } from '../StandaloneRestrictedPageOrSpace/index.js';
import { preloadLivePagesComponents } from '../../preloaders/index.js';
import { LazyCreateDialogQueryRenderer } from '../CreateDialogQueryRenderer/index.js';
import { LazyCreateDialogBlueprintWizardRenderer } from '../PseudoWizardCreateDialog/index.js';
import LegacyCssOverrides from '../../tech-debt/LegacyCssOverrides/index.js';

import { showMainContainer } from './fix-jank-ui.js';
import { HideLegacyElements } from './HideLegacyElements.js';
import { PortalsForLegacyPages } from './PortalsForLegacyPages.js';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TopNavSpacer = styled.div({
	display: 'block',
	width: '100%',
	height: '70px',
});

export default shortcutContext('global')(
	class StandaloneConfluenceNavigation extends PureComponent {
		static displayName = 'StandaloneConfluenceNavigation';
		static propTypes = {
			isAdminScreen: PropTypes.bool,
			isSpaceScreen: PropTypes.bool,
			isSpaceSettingsScreen: PropTypes.bool,
			route: PropTypes.object,
			spaceKey: PropTypes.string,
		};

		componentDidMount() {
			const { isAdminScreen } = this.props;

			// if admin screen, show content as we mount
			if (isAdminScreen) {
				showMainContainer();
			} else {
				setTimeout(showMainContainer, 300);
			}

			getSessionData();
		}

		spacer = () => <TopNavSpacer data-test-id="top-nav-spacer" />;

		render() {
			const { isAdminScreen, isSpaceSettingsScreen, isSpaceScreen, route, spaceKey } = this.props;

			let navView;
			if (getIsNav4Enabled() && isSpaceSettingsScreen) {
				navView = CONTAINER_ADMIN_HOME_ID;
			} else if (isSpaceSettingsScreen || isSpaceScreen) {
				navView = CONTAINER_HOME_ID;
			} else if (isAdminScreen) {
				navView = ADMIN_HOME_ID;
			} else if (route?.name !== EXTERNAL_SHARE.name) {
				// XXX EXTERNAL_SHARE does not have the typical navigation.
				navView = PRODUCT_HOME_ID;
			}

			return (
				<Fragment>
					<StandaloneRestrictedPageOrSpace />
					<PortalsForLegacyPages
						spaceKey={spaceKey}
						isSpaceSettingsScreen={isSpaceSettingsScreen}
					/>
					<CopySpacePermissionsForLegacyNewSpace />
					<AtlaskitAnalyticsListener client={getAnalyticsWebClient()}>
						{/* These preloaders needs to be passed here because next components cannot import classic modules */}
						<EditPageLoadingContextProvider
							nativeEditorComponentsPreloader={nativeEditorComponentsPreloader}
							livePageComponentsPreloader={preloadLivePagesComponents}
						>
							<ErrorBoundary attribution={Attribution.DISCO}>
								<ChangeboardingManager>
									<Screen
										screenEvent={{
											name: 'serverRenderedScreen',
											attributes: {
												navdexPointType: START_TOUCH,
											},
										}}
										pageState={{ spaceKey, contentId: SPACE_OVERVIEW }}
									>
										{navView && (
											<PageLayout
												view={navView}
												isSpaceSettingsScreen={isSpaceSettingsScreen}
												standalone
											/>
										)}
										<CreateDialogContextProvider>
											<LazyCreateDialogQueryRenderer />
											<LazyCreateDialogBlueprintWizardRenderer />
										</CreateDialogContextProvider>
										<LegacyCssOverrides />
										<SingleModalContainer />
										{isSpaceSettingsScreen && (
											<EditSpaceLogo spaceKey={spaceKey} entryPoint="spaceSettings" />
										)}
										<HideLegacyElements />
										<NotificationLandedEvent />
									</Screen>
								</ChangeboardingManager>
							</ErrorBoundary>
						</EditPageLoadingContextProvider>
					</AtlaskitAnalyticsListener>
					<ChangeEditionLegacyCoordinator />
				</Fragment>
			);
		}
	},
);
